import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Calendar, Sparkles, Radio } from "lucide-react";
import { api, resolveAudio } from "@/lib/api";
import SocialShare from "@/components/SocialShare";

/**
 * Renders markdown body with a single Ezoic placeholder (ID 101) inserted
 * after the first H2 section of the post. We call `ezstandalone.showAds(101)`
 * inside a useEffect so React SPA navigation still triggers ad fill on every
 * post view. The placeholder only renders when `ads_enabled=true` on the post
 * (owner is on the `pulse_earn` plan) — free/regular posts stay ad-free.
 */
function PulsePostBodyWithAds({ md }) {
  const parts = (md || "").split(/(?=^##\s)/m);
  // We only have one Ezoic placeholder ID (101). Drop it exactly once,
  // right after the first H2 section for the highest-viewability slot.
  const insertAfter = parts.length > 1 ? 1 : 0;

  useEffect(() => {
    // Ezoic script (ezstandalone) is loaded globally in index.html. On SPA
    // navigation we still need to push a showAds() command for each mount.
    try {
      if (typeof window !== "undefined" && window.ezstandalone && window.ezstandalone.cmd) {
        window.ezstandalone.cmd.push(function () {
          window.ezstandalone.showAds(101);
        });
      }
    } catch (_) { /* no-op — ads simply won't render */ }
  }, []);

  return (
    <>
      {parts.map((chunk, i) => (
        <React.Fragment key={i}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{chunk}</ReactMarkdown>
          {i === insertAfter && i < parts.length - 1 && (
            <div className="my-8" data-testid="pulse-ezoic-slot-101">
              <div
                id="ezoic-pub-ad-placeholder-101"
                className="ezoic-ad-placeholder min-h-[90px]"
              />
            </div>
          )}
        </React.Fragment>
      ))}
    </>
  );
}

export default function PulsePost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/pulse/posts/by-slug/${slug}`);
        if (!alive) return;
        setPost(data);
        // SEO meta
        document.title = `${data.title} — Fliboe Pulse`;
        const desc = document.querySelector('meta[name="description"]');
        if (desc && data.meta_description) desc.setAttribute("content", data.meta_description);
        // Open Graph (best-effort runtime injection — proper SSR comes later)
        const setMeta = (prop, content, attr = "property") => {
          if (!content) return;
          let el = document.querySelector(`meta[${attr}="${prop}"]`);
          if (!el) {
            el = document.createElement("meta");
            el.setAttribute(attr, prop);
            document.head.appendChild(el);
          }
          el.setAttribute("content", content);
        };
        setMeta("og:title", data.title);
        setMeta("og:description", data.meta_description);
        if (data.cover_url) setMeta("og:image", resolveAudio(data.cover_url));
        setMeta("og:type", "article");
        setMeta("article:published_time", data.published_at || "");
        setMeta("article:modified_time", data.updated_at || data.published_at || "");
        setMeta("article:author", data.author?.name || "Fliboe Pulse Editorial");
        // Canonical URL — prevents dup-content penalties if the same post is
        // linked from multiple sub-app pages.
        const canonicalHref = `${window.location.origin}/p/${slug}`;
        let can = document.querySelector('link[rel="canonical"]');
        if (!can) { can = document.createElement("link"); can.setAttribute("rel", "canonical"); document.head.appendChild(can); }
        can.setAttribute("href", canonicalHref);
        // JSON-LD Article + BreadcrumbList — unlocks rich snippets, article
        // carousels, and E-E-A-T signals for Google's ranking algorithm.
        const jsonLd = {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Article",
              "@id": canonicalHref + "#article",
              "isPartOf": { "@id": canonicalHref },
              "headline": data.title,
              "description": data.meta_description,
              "image": data.cover_url ? resolveAudio(data.cover_url) : undefined,
              "datePublished": data.published_at,
              "dateModified": data.updated_at || data.published_at,
              "author": {
                "@type": "Person",
                "name": data.author?.name || "Fliboe Pulse Editorial",
                "description": data.author?.bio,
              },
              "publisher": {
                "@type": "Organization",
                "name": "Fliboe",
                "logo": { "@type": "ImageObject", "url": `${window.location.origin}/icon-512.png` },
              },
              "mainEntityOfPage": canonicalHref,
              "keywords": data.keyword,
              "articleSection": data.site?.niche,
              "inLanguage": "en",
            },
            {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Fliboe", "item": window.location.origin + "/" },
                { "@type": "ListItem", "position": 2, "name": "Pulse", "item": window.location.origin + "/pulse" },
                { "@type": "ListItem", "position": 3, "name": data.title, "item": canonicalHref },
              ],
            },
          ],
        };
        // Inject / replace the LD JSON block
        let ld = document.getElementById("pulse-post-jsonld");
        if (!ld) {
          ld = document.createElement("script");
          ld.type = "application/ld+json";
          ld.id = "pulse-post-jsonld";
          document.head.appendChild(ld);
        }
        ld.textContent = JSON.stringify(jsonLd);
      } catch (e) {
        if (alive) setErr(e?.response?.status === 404 ? "Post not found" : "Failed to load post");
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  if (err) {
    return (
      <main className="max-w-2xl mx-auto px-5 py-32 text-center" data-testid="pulse-post-error">
        <h1 className="fl-display text-3xl font-bold text-white">{err}</h1>
        <Link to="/pulse" className="fl-btn mt-6 inline-flex">Back to Pulse</Link>
      </main>
    );
  }
  if (!post) {
    return <main className="max-w-2xl mx-auto px-5 py-32 text-white/40">Loading…</main>;
  }

  return (
    <article className="max-w-3xl mx-auto px-5 py-10 sm:py-16" data-testid="pulse-post">
      <Link to="/" className="text-white/50 text-sm inline-flex items-center gap-1 hover:text-white mb-6">
        <ArrowLeft className="w-4 h-4" /> Fliboe FM
      </Link>

      {post.cover_url && (
        <img
          src={resolveAudio(post.cover_url)}
          alt=""
          className="w-full aspect-[16/9] object-cover rounded-2xl border border-white/10 mb-8"
          data-testid="pulse-post-cover"
        />
      )}

      <div className="text-xs text-fuchsia-300/80 uppercase tracking-widest font-semibold inline-flex items-center gap-1.5">
        <Sparkles className="w-3.5 h-3.5" /> {post.site?.niche || "Fliboe Pulse"}
      </div>
      <h1 className="mt-3 text-4xl sm:text-5xl font-black tracking-tight text-white leading-[1.05]" data-testid="pulse-post-title">
        {post.title}
      </h1>
      <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-white/50">
        <span className="inline-flex items-center gap-1" data-testid="pulse-post-author">
          <span className="w-1.5 h-1.5 rounded-full bg-fuchsia-400" />
          By <strong className="text-white/80 font-semibold">{post.author?.name || "Fliboe Pulse Editorial"}</strong>
        </span>
        <span>·</span>
        <span className="inline-flex items-center gap-1">
          <Calendar className="w-3.5 h-3.5" />
          {post.published_at ? new Date(post.published_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : ""}
        </span>
        {post.updated_at && post.updated_at !== post.published_at && (
          <>
            <span>·</span>
            <span className="text-emerald-300" data-testid="pulse-post-updated">Updated {new Date(post.updated_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</span>
          </>
        )}
        <span>·</span>
        <span>Published on <Link to="/pulse/about" className="hover:text-white underline decoration-dotted underline-offset-2">{post.site?.name || "Fliboe Pulse"}</Link></span>
      </div>

      <div className="mt-6">
        <SocialShare
          url={typeof window !== "undefined" ? window.location.href : ""}
          title={post.title}
          summary={post.meta_description}
        />
      </div>

      <div className="pulse-prose mt-10" data-testid="pulse-post-body">
        {post.ads_enabled ? (
          <PulsePostBodyWithAds md={post.body_md || ""} />
        ) : (
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.body_md || ""}</ReactMarkdown>
        )}
      </div>

      {/* Bottom share row — easier to spot for readers who scroll past the article */}
      <div className="mt-12 pt-8 border-t border-white/10">
        <SocialShare
          url={typeof window !== "undefined" ? window.location.href : ""}
          title={post.title}
          summary={post.meta_description}
        />
      </div>

      <div className="mt-14 fl-card p-6 flex flex-wrap items-center gap-4 justify-between" data-testid="pulse-post-cta">
        {post.listen_live ? (
          <>
            <div>
              <div className="text-xs uppercase tracking-widest text-emerald-300/90 font-semibold inline-flex items-center gap-2">
                <span className="live-dot" /> Live right now on Fliboe
              </div>
              <div className="text-white font-bold text-lg mt-1">Listen to {post.listen_live.name}</div>
              <div className="text-white/60 text-sm mt-1">{post.listen_live.tagline || `${post.listen_live.genre} — broadcasting now.`}</div>
            </div>
            <Link
              to={post.listen_live.url}
              className="fl-btn"
              data-testid="pulse-post-listen-live"
            >
              <Radio className="w-4 h-4" /> Tune in
            </Link>
          </>
        ) : (
          <>
            <div>
              <div className="text-xs uppercase tracking-widest text-fuchsia-300/80 font-semibold">Built on Fliboe</div>
              <div className="text-white font-bold text-lg mt-1">Launch your own internet radio station.</div>
              <div className="text-white/60 text-sm mt-1">AI DJ. Worldwide sync. Tip jar. Free to launch.</div>
            </div>
            <Link to="/" className="fl-btn">
              <Radio className="w-4 h-4" /> Get started
            </Link>
          </>
        )}
      </div>

      <style>{`
        .pulse-prose { color: rgba(255,255,255,0.85); font-size: 1.075rem; line-height: 1.78; }
        .pulse-prose h1, .pulse-prose h2, .pulse-prose h3, .pulse-prose h4 { color: #fff; font-weight: 800; letter-spacing: -0.01em; line-height: 1.2; }
        .pulse-prose h2 { font-size: 1.75rem; margin-top: 2.4em; margin-bottom: 0.6em; }
        .pulse-prose h3 { font-size: 1.3rem; margin-top: 1.8em; margin-bottom: 0.5em; }
        .pulse-prose p { margin: 1em 0; }
        .pulse-prose a { color: #34d399; text-decoration: underline; text-underline-offset: 3px; }
        .pulse-prose a:hover { color: #fde68a; }
        .pulse-prose strong { color: #fff; font-weight: 700; }
        .pulse-prose ul, .pulse-prose ol { padding-left: 1.4em; margin: 1em 0; }
        .pulse-prose li { margin: 0.45em 0; }
        .pulse-prose blockquote { border-left: 3px solid #d946ef; padding-left: 1em; color: rgba(255,255,255,0.78); font-style: italic; margin: 1.5em 0; }
        .pulse-prose code { background: rgba(255,255,255,0.06); color: #fde68a; border-radius: 6px; padding: 0.15em 0.45em; font-size: 0.92em; }
        .pulse-prose pre { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 10px; padding: 1em; overflow-x: auto; }
        .pulse-prose pre code { background: transparent; padding: 0; }
        .pulse-prose hr { border-color: rgba(255,255,255,0.10); margin: 2.4em 0; }
        .pulse-prose img { border-radius: 12px; max-width: 100%; margin: 1.5em 0; }
        .pulse-prose table { width: 100%; border-collapse: collapse; margin: 1.5em 0; }
        .pulse-prose th, .pulse-prose td { border: 1px solid rgba(255,255,255,0.10); padding: 0.6em 0.8em; text-align: left; }
        .pulse-prose th { background: rgba(255,255,255,0.04); font-weight: 600; }
      `}</style>
    </article>
  );
}
